// ui.js - shared UI primitives: screen switching, toast, modal, promptText,
// player card / level badge / coach banner. Higher-level controllers
// (app.js, dashboard.js, selection.js, evaluation.js) own their own DOM
// rendering and lean on the helpers here for the patterns that recur.

import { APP } from './app.js';
import { CONFIG } from './config.js';
import { getDescriptors, localizeSkill } from './evaluation-data.js';
import { t } from './strings.js';
import { UTILS } from './utils.js';

export const UI = (() => {

  // ── SCREEN MANAGEMENT ────────────────────────────────────────────────────────

  /** Currently visible screen id */
  let _currentScreen = null;

  // Show a screen by id, hiding all others.
  function showScreen(screenId) {
    document.querySelectorAll('.screen').forEach(s => s.classList.remove('active'));
    const target = document.getElementById(screenId);
    if (target) {
      target.classList.add('active');
      _currentScreen = screenId;
      // Scroll to top on screen change
      target.scrollTop = 0;
    }
  }

  function currentScreen() { return _currentScreen; }

  // ── TOAST NOTIFICATIONS ──────────────────────────────────────────────────────

  let _toastTimer = null;

  // Show a brief toast notification.
  function toast(message, type = 'info', duration = 2500) {
    const el = document.getElementById('toast');
    if (!el) return;
    if (_toastTimer) clearTimeout(_toastTimer);
    el.textContent = message;
    el.className = `toast toast--${type} toast--visible`;
    _toastTimer = setTimeout(() => {
      el.className = 'toast';
    }, duration);
  }

  // ── MODAL ────────────────────────────────────────────────────────────────────

  // Show a modal dialog. `body` renders as plain text; `html` (optional)
  // renders as markup - the caller must escapeHtml every interpolated value.
  function modal({ title, body, html, buttons = [] }) {
    const titleEl   = document.getElementById('modal-title');
    const bodyEl    = document.getElementById('modal-body');
    const buttonsEl = document.getElementById('modal-buttons');
    const overlay   = document.getElementById('modal-overlay');

    if (!overlay) return;

    titleEl.textContent = title || '';
    if (html != null) bodyEl.innerHTML = html;
    else bodyEl.textContent = body || '';
    const esc = UTILS.escapeHtml;
    buttonsEl.innerHTML = buttons.map((btn, i) => `
      <button class="modal-btn ${btn.style ? `modal-btn--${esc(btn.style)}` : ''}"
              data-modal-btn="${i}">
        ${esc(btn.label)}
      </button>
    `).join('');

    buttonsEl.querySelectorAll('[data-modal-btn]').forEach(btnEl => {
      btnEl.addEventListener('click', () => {
        const idx = parseInt(btnEl.dataset.modalBtn);
        closeModal();
        if (buttons[idx]?.action) buttons[idx].action();
      });
    });

    overlay.classList.remove('modal--hidden');
  }

  function closeModal() {
    document.getElementById('modal-overlay')?.classList.add('modal--hidden');
  }

  // Styled text-prompt modal - a replacement for `prompt()` that fits the app's dark theme and works reasonably...
  function promptText({ title, label, value = '', placeholder = '', rows = 4, singleLine = false, onSubmit, onCancel, onClear }) {
    const esc = UTILS.escapeHtml;
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    const inputHtml = singleLine
      ? `<input type="text" class="field prompt-input" placeholder="${esc(placeholder)}" value="${esc(value)}">`
      : `<textarea class="prompt-input" rows="${rows}" placeholder="${esc(placeholder)}">${esc(value)}</textarea>`;
    overlay.innerHTML = `
      <div class="modal">
        <div class="modal__title">${esc(title)}</div>
        <div class="modal__body">
          ${label ? `<div class="prompt-label">${esc(label)}</div>` : ''}
          ${inputHtml}
        </div>
        <div class="modal__buttons">
          ${onClear ? `<button class="modal-btn modal-btn--danger" data-action="clear">Clear</button>` : ''}
          <button class="modal-btn" data-action="cancel">Cancel</button>
          <button class="modal-btn modal-btn--primary" data-action="save">Save</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const input = overlay.querySelector('.prompt-input');
    input.focus();
    const len = input.value.length;
    if (input.setSelectionRange) input.setSelectionRange(len, len);

    let resolved = false;
    const close = () => overlay.remove();
    const cancel = () => {
      if (resolved) return;
      resolved = true;
      close();
      onCancel?.();
    };
    overlay.addEventListener('click', e => { if (e.target === overlay) cancel(); });
    overlay.querySelector('[data-action="cancel"]').onclick = cancel;
    overlay.querySelector('[data-action="save"]').onclick = () => {
      if (resolved) return;
      resolved = true;
      const v = input.value.trim();
      close();
      onSubmit?.(v);
    };
    if (onClear) {
      overlay.querySelector('[data-action="clear"]').onclick = () => {
        if (resolved) return;
        resolved = true;
        close();
        onClear();
      };
    }
    // Enter submits on single-line; Esc cancels everywhere.
    if (singleLine) {
      input.addEventListener('keydown', e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          overlay.querySelector('[data-action="save"]').click();
        }
      });
    }
    document.addEventListener('keydown', function onKey(e) {
      if (e.key === 'Escape') {
        cancel();
        document.removeEventListener('keydown', onKey);
      }
    });
  }

  // Promise-returning wrapper around promptText.
  function promptTextAsync(opts) {
    return new Promise(resolve => {
      promptText({
        ...opts,
        onSubmit: v => resolve(v),
        onCancel: () => resolve(null),
      });
    });
  }

  // ── LOADING STATE ────────────────────────────────────────────────────────────

  // Set a container to a loading state.
  function setLoading(containerId, message = '') {
    const el = document.getElementById(containerId);
    if (!el) return;
    el.innerHTML = `
      <div class="loading-state">
        <div class="spinner"></div>
        ${message ? `<p class="loading-state__message">${UTILS.escapeHtml(message)}</p>` : ''}
      </div>
    `;
  }

  // Set a container to an empty state.
  function setEmpty(containerId, message, actionLabel, actionFn) {
    const el = document.getElementById(containerId);
    if (!el) return;
    const esc = UTILS.escapeHtml;
    el.innerHTML = `
      <div class="empty-state">
        <p class="empty-state__message">${esc(message)}</p>
        ${actionLabel && actionFn ? `
          <button class="btn btn--primary" id="empty-state-action">${esc(actionLabel)}</button>
        ` : ''}
      </div>
    `;
    if (actionLabel && actionFn) {
      document.getElementById('empty-state-action')?.addEventListener('click', actionFn);
    }
  }

  // ── PLAYER LIST ITEMS ────────────────────────────────────────────────────────

  // Render a player list item.
  function playerListItem(player, evalRecords = [], isDirector, isMine = () => false) {
    const esc = UTILS.escapeHtml;
    const initials = UTILS.initials(player.name);
    const total = evalRecords.length;

    let statusText = 'not rated';
    let isDone = false;

    if (isDirector) {
      if (total === 1) {
        const rec = evalRecords[0];
        statusText = `✓ by ${rec.coach_name} · ${UTILS.formatDate(rec.evaluated_at, 'short')}`;
        isDone = true;
      } else if (total > 1) {
        statusText = `✓ ${total} evaluations`;
        isDone = true;
      }
    } else {
      const myRec = evalRecords.find(r => isMine(r));
      if (myRec) {
        statusText = '✓ rated by you';
        isDone = true;
      }
    }

    return `
      <div class="player-item ${isDone ? 'player-item--rated' : ''}"
           data-player-id="${esc(player.id)}"
           data-player-index="${esc(player._index ?? '')}">
        <div class="player-item__avatar ${isDone ? 'player-item__avatar--rated' : ''}">
          ${esc(initials)}
        </div>
        <div class="player-item__info">
          <div class="player-item__name">${esc(player.name)}</div>
          <div class="player-item__status ${isDone ? 'player-item__status--done' : ''}">
            ${esc(statusText)}
          </div>
        </div>
        <div class="player-item__chevron">›</div>
      </div>
    `;
  }

  // ── DOMAIN TABS ──────────────────────────────────────────────────────────────

  // Render domain tab bar.
  function domainTabs(domains, activeDomain, ratedDomains = {}, showFeedback = true) {
    const esc = UTILS.escapeHtml;
    const tabs = showFeedback ? [...domains, 'Feedback'] : domains;
    return tabs.map(domain => {
      const isFeedback  = domain === 'Feedback';
      const isActive    = domain === activeDomain;
      const hasRatings  = !isFeedback && ratedDomains[domain];
      return `
        <button class="domain-tab
                        ${isActive    ? 'domain-tab--active'    : ''}
                        ${isFeedback  ? 'domain-tab--feedback'  : ''}
                        ${hasRatings && !isActive ? 'domain-tab--has-ratings' : ''}"
                data-domain="${esc(domain)}">
          ${esc(domain)}
        </button>
      `;
    }).join('');
  }

  // ── LEVEL BADGE ──────────────────────────────────────────────────────────────

  // Render a level badge pill.
  function levelBadge(level) {
    const levelName = CONFIG.scoring.levelNames[level - 1] || '';
    const safeLevel = Number.isInteger(level) ? level : 0;
    return `<span class="level-badge level-badge--${safeLevel}">${UTILS.escapeHtml(levelName.toUpperCase())}</span>`;
  }

  // ── SKILL ROWS ────────────────────────────────────────────────────────────────

  /**
   * Render a single skill card for the evaluation screen.
   *
   * TWO MODES, ONE COMPONENT. Normally it marks ONE player against the five
   * descriptors. In BAND mode (`band` supplied) it marks a whole TEAM instead:
   * the floor is the weakest child in the squad and the ceiling the strongest,
   * so the card reads as the span of descriptors this squad covers. The
   * descriptors are the whole point of banding this way, and they are already
   * here, which is why this is an extra mode rather than a second component.
   *
   * @param {object}  [band]          { bottom, top } to render band mode
   * @param {number}  [pendingLevel]  the first of the two band taps, awaiting
   *                                  its pair (see band-step.js)
   */
  function skillCard({ domain, skill, ageGroup, selectedLevel = 0, comment = '', maxLevel = 5,
                       referenceLevel = 0, band = null, pendingLevel = 0 }) {
    const esc = UTILS.escapeHtml;
    const descriptors = getDescriptors(domain, skill, ageGroup);
    const skillId = `${domain}-${skill}`.replace(/[^a-z0-9]/gi, '-').toLowerCase();
    const cap = Math.max(1, Math.min(5, maxLevel | 0));
    const banding = !!band;
    // Mid-way through the two taps: show only the mark just made, so a stale
    // band cannot be read as the one being set.
    const half = banding && pendingLevel > 0;

    const levelsHtml = descriptors.map((desc, i) => {
      const level = i + 1;
      const isBlocked  = level > cap;
      const isReference = !banding && level === referenceLevel;

      let bandClass = '';
      if (banding) {
        if (half) {
          bandClass = level === pendingLevel ? ' level-btn--band-edge level-btn--band-pending' : '';
        } else if (level === band.bottom || level === band.top) {
          bandClass = ' level-btn--band-edge';
        } else if (level > band.bottom && level < band.top) {
          bandClass = ' level-btn--band-inside';
        }
      }
      const isSelected = !banding && level === selectedLevel;

      // In band mode the edge marks say WHICH end they are, because "2 to 4"
      // tells you nothing about which child you were thinking of.
      let edgeTag = '';
      if (banding && !half && level === band.bottom) edgeTag = t('bandstep.weakest');
      if (banding && !half && level === band.top)    edgeTag = t('bandstep.strongest');
      if (banding && !half && band.bottom === band.top && level === band.bottom) {
        edgeTag = t('bandstep.allAlike');
      }

      return `
        <div class="level-btn ${isSelected ? `level-btn--selected level-btn--l${level}` : ''} ${isBlocked ? 'level-btn--blocked' : ''}${bandClass}"
             data-domain="${esc(domain)}"
             data-skill="${esc(skill)}"
             data-level="${level}"
             ${isBlocked ? 'aria-disabled="true" title="Above director ceiling"' : ''}>
          ${isReference ? '<div class="level-btn__ref-dot" title="Reference coach rated this level"></div>' : ''}
          <div class="level-btn__num">${level}</div>
          <div class="level-btn__text">
            <div class="level-btn__label">${esc(CONFIG.scoring.levelNames[i])}${
              edgeTag ? ` <span class="level-btn__edge">${esc(edgeTag)}</span>` : ''}</div>
            <div class="level-btn__desc">${esc(desc)}</div>
          </div>
        </div>
      `;
    }).join('');

    // A band has no per-criterion comment: it is a statement about a squad,
    // and there is nobody for the note to be about.
    if (banding) {
      return `
        <div class="skill-card skill-card--band" data-skill="${esc(skill)}">
          <div class="skill-card__name">${esc(localizeSkill(skill))}</div>
          ${cap < 5 ? `<div class="skill-card__ceiling-hint">${esc(t('bandstep.cap', { n: cap }))}</div>` : ''}
          <div class="skill-card__levels">${levelsHtml}</div>
        </div>
      `;
    }

    const hasComment = comment && comment.trim();
    const ceilingHint = cap < 5
      ? `<div class="skill-card__ceiling-hint">Director's max for this team: ${cap}</div>`
      : '';

    return `
      <div class="skill-card" data-skill="${esc(skill)}">
        <div class="skill-card__name">${esc(skill)}</div>
        ${ceilingHint}
        <button class="skill-card__comment-toggle ${hasComment ? 'skill-card__comment-toggle--open' : ''}"
                data-skill-id="${esc(skillId)}">
          ${hasComment ? 'hide comment' : '+ add comment'}
        </button>
        <div class="skill-card__comment-box ${hasComment ? 'skill-card__comment-box--open' : ''}"
             id="comment-box-${esc(skillId)}">
          <textarea class="skill-card__comment-input"
                    placeholder="Comment on ${esc(skill)}..."
                    data-domain="${esc(domain)}"
                    data-skill="${esc(skill)}">${esc(comment)}</textarea>
        </div>
        <div class="skill-card__levels">${levelsHtml}</div>
      </div>
    `;
  }

  // ── FEEDBACK PILLAR ──────────────────────────────────────────────────────────

  // Render the feedback pillar (strong points, working points, notes).
  /**
   * The Feedback tab. As well as the three free-text blocks it carries the two
   * NOMINATIONS: the one skill the coach rates as this player's best and the
   * one most needing work, chosen from the skills they actually rated here.
   *
   * Only rated skills are offered, because nominating something you did not
   * assess is a judgment with nothing behind it. With no ratings yet the
   * pickers say so rather than presenting an empty dropdown.
   */
  function feedbackPillar({ strongPoints = '', workingPoints = '', sessionNotes = '',
                            ratedSkills = [], nominatedStrength = '', nominatedWorking = '' }) {
    const esc = UTILS.escapeHtml;
    const opts = (selected) =>
      `<option value="">Not chosen</option>` +
      ratedSkills.map(sk =>
        `<option value="${esc(sk)}"${sk === selected ? ' selected' : ''}>${esc(sk)}</option>`).join('');
    const nominations = !ratedSkills.length
      ? `<div class="feedback-card">
           <div class="feedback-card__label">Best and weakest</div>
           <div class="feedback-card__hint">Rate some skills first, then come back and name the best and the weakest.</div>
         </div>`
      : `<div class="feedback-card feedback-card--nominate">
           <div class="feedback-card__label">Best and weakest</div>
           <div class="feedback-card__hint">Pick one of each from what you just rated. These are what the player sees as their strengths and working points.</div>
           <label class="nom-row">
             <span class="nom-row__label">Best skill</span>
             <select id="nominate-strength" class="nom-row__select">${opts(nominatedStrength)}</select>
           </label>
           <label class="nom-row">
             <span class="nom-row__label">Needs most work</span>
             <select id="nominate-working" class="nom-row__select">${opts(nominatedWorking)}</select>
           </label>
         </div>`;
    return `
      <div class="feedback-pillar">
        ${nominations}
        <div class="feedback-card feedback-card--strong">
          <div class="feedback-card__label">Strong points</div>
          <div class="feedback-card__hint">What does this player do consistently well?</div>
          <textarea id="feedback-strong"
                    class="feedback-card__input"
                    rows="4"
                    placeholder="e.g. Great first touch, excellent work rate...">${esc(strongPoints)}</textarea>
        </div>
        <div class="feedback-card feedback-card--working">
          <div class="feedback-card__label">Working points</div>
          <div class="feedback-card__hint">What should this player focus on developing?</div>
          <textarea id="feedback-working"
                    class="feedback-card__input"
                    rows="4"
                    placeholder="e.g. Ball carrying head position, tackling timing...">${esc(workingPoints)}</textarea>
        </div>
        <div class="feedback-card feedback-card--notes">
          <div class="feedback-card__label">Session notes</div>
          <div class="feedback-card__hint">Any additional context from this session.</div>
          <textarea id="feedback-notes"
                    class="feedback-card__input"
                    rows="3"
                    placeholder="Optional...">${esc(sessionNotes)}</textarea>
        </div>
      </div>
    `;
  }

  // ── PROGRESS BAR ─────────────────────────────────────────────────────────────

  // Update the progress info text.
  function updateProgress(elementId, rated, total) {
    const el = document.getElementById(elementId);
    if (el) el.textContent = `${rated} of ${total} criteria rated`;
  }

  // ── COACH BANNER ─────────────────────────────────────────────────────────────

  // Show or hide the coach banner.
  function setCoachBanner(show, text = '') {
    const el = document.getElementById('coach-banner');
    if (!el) return;
    if (show) {
      // Banner is layout-wise a single block; render the text plus a small
      // sign-out link on the right so coaches can switch device contexts.
      el.innerHTML = `
        <span class="coach-banner__text">${UTILS.escapeHtml(text)}</span>
        <button class="coach-banner__signout" id="coach-banner-signout"
                style="float:right;background:none;border:none;color:inherit;opacity:0.7;font-size:11px;cursor:pointer;padding:2px 6px;">
          Sign out
        </button>
      `;
      el.classList.remove('coach-banner--hidden');
      document.getElementById('coach-banner-signout')?.addEventListener('click', () => {
        if (typeof APP !== 'undefined' && APP.signOut) APP.signOut();
      });
    } else {
      el.classList.add('coach-banner--hidden');
    }
  }

  // ── LINE FILTER PILL ────────────────────────────────────────────────────────
  //
  // Tri-state pill (All / Girls / Boys) shared across Home, Manage, Calibrate,
  // and Dashboard. Hidden entirely when the team list is single-line (until
  // boys teams exist there's nothing to filter, so the pill stays out of the
  // way). Caller passes the container element ID and an onChange callback.

  // Initialise a line-filter pill.
  function setupLineFilter(containerId, teams, currentValue, onChange) {
    const el = document.getElementById(containerId);
    if (!el) return currentValue;
    const lines = new Set((teams || []).map(t => t.line || 'girls'));
    // Hide when only one line is present - no value in showing a filter.
    if (lines.size <= 1) {
      el.style.display = 'none';
      return 'all';
    }
    el.style.display = 'flex';
    const value = ['all', 'girls', 'boys'].includes(currentValue) ? currentValue : 'all';
    el.querySelectorAll('.line-filter__btn').forEach(btn => {
      btn.classList.toggle('line-filter__btn--active', btn.dataset.line === value);
    });
    // Clone-replace strips old handlers so repeat calls don't stack listeners.
    el.querySelectorAll('.line-filter__btn').forEach(btn => {
      const next = btn.cloneNode(true);
      btn.replaceWith(next);
      next.addEventListener('click', () => {
        const v = next.dataset.line;
        el.querySelectorAll('.line-filter__btn').forEach(b =>
          b.classList.toggle('line-filter__btn--active', b === next));
        onChange?.(v);
      });
    });
    return value;
  }

  // ── UNSAVED CHANGES GUARD ────────────────────────────────────────────────────

  // Show confirmation modal before navigating away from unsaved data.
  function confirmNavAway(onConfirm) {
    modal({
      title: 'Unsaved ratings',
      body: 'Navigating away will lose your current ratings. Are you sure?',
      buttons: [
        // Safe option first - easier tap target for the non-destructive choice.
        { label: 'Keep rating', style: 'primary' },
        { label: 'Go back and lose ratings', style: 'danger', action: onConfirm },
      ],
    });
  }

  // ── PUBLIC API ────────────────────────────────────────────────────────────────

  // ── LANGUAGE (EN/NL) ─────────────────────────────────────────────────────────
  // The choice is persisted and applied on the next load (config.js reads it at
  // boot). A reload is the simplest way to re-render every screen in the new
  // language with no half-translated in-memory state.
  function setLang(lang) {
    if (lang !== 'en' && lang !== 'nl') return;
    try { localStorage.setItem('khc_lang', lang); } catch { /* ignore */ }
    window.location.reload();
  }

  // Wire a container of [data-lang] buttons: highlight the active one, switch on
  // click. Reusable on the gate and inside the player / coach homes.
  function wireLangToggle(container) {
    if (!container) return;
    const cur = CONFIG.i18n?.lang || 'en';
    container.querySelectorAll('[data-lang]').forEach(b => {
      b.classList.toggle('lang-toggle__btn--on', b.dataset.lang === cur);
      b.addEventListener('click', () => setLang(b.dataset.lang));
    });
  }

  // Standard two-button EN/NL markup for injecting into a header.
  const langToggleHtml = () => `
    <div class="lang-toggle">
      <button type="button" class="lang-toggle__btn" data-lang="en">EN</button>
      <button type="button" class="lang-toggle__btn" data-lang="nl">NL</button>
    </div>`;

  return {
    showScreen,
    currentScreen,
    setLang,
    wireLangToggle,
    langToggleHtml,
    toast,
    modal,
    promptText,
    promptTextAsync,
    closeModal,
    setLoading,
    setEmpty,
    setupLineFilter,
    playerListItem,
    domainTabs,
    levelBadge,
    skillCard,
    feedbackPillar,
    updateProgress,
    setCoachBanner,
    confirmNavAway,
  };

})();
